import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useMy } from '@/hooks/useMy';
import { useApproveProgram } from '@/sections/customerManagement/hooks/useApproveProgram';
import { useBountyCatalog } from '@/sections/customerManagement/hooks/useBountyCatalog';
import type { CatalogProgram } from '@/sections/customerManagement/types';

type SortableColumn =
  | 'name'
  | 'platform'
  | 'offers_bounties'
  | 'scope_count'
  | 'response_efficiency_pct';

interface SortState {
  column: SortableColumn;
  descending: boolean;
}

const MAX_VISIBLE_SCOPE_BADGES = 3;

function parseScopeTypeSummary(
  summary: string
): Array<{ count: string; type: string }> {
  if (!summary || !summary.trim()) return [];
  return summary
    .split(', ')
    .map(entry => {
      const spaceIndex = entry.indexOf(' ');
      if (spaceIndex === -1) return { count: '', type: entry.trim() };
      return {
        count: entry.slice(0, spaceIndex),
        type: entry.slice(spaceIndex + 1),
      };
    })
    .filter(item => item.type);
}

function formatEfficiency(pct?: number): string {
  if (pct == null) return '—';
  return `${pct}%`;
}

function ScopeBadges({ summary }: { summary: string }) {
  const items = parseScopeTypeSummary(summary);
  if (items.length === 0)
    return <span className="text-chariot-text-secondary">-</span>;

  const visible = items.slice(0, MAX_VISIBLE_SCOPE_BADGES);
  const remaining = items.length - MAX_VISIBLE_SCOPE_BADGES;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((item, i) => (
        <Badge
          key={i}
          variant="secondary"
          className="text-[10px] whitespace-nowrap"
        >
          {item.count} {item.type}
        </Badge>
      ))}
      {remaining > 0 && (
        <span
          className="text-chariot-text-secondary text-[10px] font-medium"
          title={items
            .slice(MAX_VISIBLE_SCOPE_BADGES)
            .map(i => `${i.count} ${i.type}`)
            .join(', ')}
        >
          +{remaining} more
        </span>
      )}
    </div>
  );
}

function compareCatalogPrograms(
  a: CatalogProgram,
  b: CatalogProgram,
  sort: SortState
): number {
  let cmp = 0;
  switch (sort.column) {
    case 'name':
      cmp = a.name.localeCompare(b.name);
      break;
    case 'platform':
      cmp = a.platform.localeCompare(b.platform);
      break;
    case 'offers_bounties':
      cmp = Number(a.offers_bounties) - Number(b.offers_bounties);
      break;
    case 'scope_count':
      cmp = a.scope_count - b.scope_count;
      break;
    case 'response_efficiency_pct': {
      const av = a.response_efficiency_pct;
      const bv = b.response_efficiency_pct;
      // Missing values always sort last, in either direction (early return skips the descending flip below).
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      cmp = av - bv;
      break;
    }
  }
  return sort.descending ? -cmp : cmp;
}

export const DiscoverPrograms: React.FC = () => {
  const [, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [programToApprove, setProgramToApprove] =
    useState<CatalogProgram | null>(null);
  const [sort, setSort] = useState<SortState>({
    column: 'name',
    descending: false,
  });

  const { canOnboardCustomer } = useEntitlements();
  const { catalog, isLoading, refreshCatalog } = useBountyCatalog();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { approveProgram, isPending } = useApproveProgram();
  const { data: handleSettings, invalidate: invalidateHandleSettings } = useMy(
    { resource: 'setting', query: 'hackerone_handle', allTenants: true },
    { doNotImpersonate: true }
  );

  const approvedHandles = useMemo(() => {
    const set = new Set<string>();
    handleSettings.forEach(s => {
      if (typeof s.value === 'string') {
        set.add(s.value);
      }
    });
    return set;
  }, [handleSettings]);

  const filteredPrograms = useMemo(() => {
    if (!search.trim()) return catalog;
    const term = search.toLowerCase();
    return catalog.filter(
      p =>
        p.name.toLowerCase().includes(term) ||
        p.handle.toLowerCase().includes(term)
    );
  }, [catalog, search]);

  const sortedPrograms = useMemo(() => {
    return [...filteredPrograms].sort((a, b) =>
      compareCatalogPrograms(a, b, sort)
    );
  }, [filteredPrograms, sort]);

  const handleSort = (column: SortableColumn) => {
    setSort(prev =>
      prev.column === column
        ? { column, descending: !prev.descending }
        : { column, descending: false }
    );
  };

  const handleApprove = async () => {
    if (!programToApprove) return;
    await approveProgram(programToApprove);
    invalidateHandleSettings();
    setProgramToApprove(null);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshCatalog();
    } catch {
      toast.error('Failed to trigger catalog refresh');
    } finally {
      setIsRefreshing(false);
    }
  };

  const sortIndicator = (column: SortableColumn) => {
    if (sort.column !== column) return null;
    return sort.descending ? (
      <ChevronDownIcon className="inline-block h-3.5 w-3.5" />
    ) : (
      <ChevronUpIcon className="inline-block h-3.5 w-3.5" />
    );
  };

  const sortableThClass =
    'text-chariot-text-secondary cursor-pointer select-none px-3 py-2 font-medium';

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSearchParams({ view: 'bug-bounty' })}
        >
          Back to Bug Bounty
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? 'Refreshing...' : 'Refresh Catalog'}
        </Button>
      </div>

      <div className="max-w-sm">
        <Input
          placeholder="Search by name or handle..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <p className="text-chariot-text-secondary text-sm">
          Loading catalog...
        </p>
      ) : filteredPrograms.length === 0 ? (
        <p className="text-chariot-text-secondary text-sm">
          No programs found.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-chariot-stroke border-b text-left">
                <th
                  className={sortableThClass}
                  onClick={() => handleSort('name')}
                >
                  Name {sortIndicator('name')}
                </th>
                <th
                  className={sortableThClass}
                  onClick={() => handleSort('platform')}
                >
                  Platform {sortIndicator('platform')}
                </th>
                <th
                  className={sortableThClass}
                  onClick={() => handleSort('offers_bounties')}
                >
                  Offers Bounties {sortIndicator('offers_bounties')}
                </th>
                <th
                  className={sortableThClass}
                  onClick={() => handleSort('response_efficiency_pct')}
                  title="Percentage of reports meeting the program's SLA targets (HackerOne 90-day average)"
                >
                  Response Efficiency {sortIndicator('response_efficiency_pct')}
                </th>
                <th
                  className={sortableThClass}
                  onClick={() => handleSort('scope_count')}
                >
                  Scope Count {sortIndicator('scope_count')}
                </th>
                <th className="text-chariot-text-secondary px-3 py-2 font-medium">
                  Scope Summary
                </th>
                <th className="text-chariot-text-secondary px-3 py-2 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedPrograms.map(program => {
                const isApproved = approvedHandles.has(program.handle);
                return (
                  <tr
                    key={program.handle}
                    className="border-chariot-stroke border-b"
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${
                            program.verified ? 'bg-green-500' : 'bg-red-500'
                          }`}
                          title={
                            program.verified
                              ? `Verified ${program.verified_at ? new Date(program.verified_at).toLocaleDateString() : ''}`
                              : 'Not verified on HackerOne'
                          }
                        />
                        {program.profile_picture_url && (
                          <img
                            src={program.profile_picture_url}
                            alt=""
                            className="h-5 w-5 shrink-0 rounded-full object-cover"
                          />
                        )}
                        <a
                          href={program.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          {program.name}
                        </a>
                      </div>
                    </td>
                    <td className="text-chariot-text-secondary px-3 py-2">
                      {program.platform}
                    </td>
                    <td className="px-3 py-2">
                      {program.offers_bounties ? (
                        <Badge variant="default">Yes</Badge>
                      ) : (
                        <Badge variant="secondary">No</Badge>
                      )}
                    </td>
                    <td className="text-chariot-text-secondary px-3 py-2">
                      <span title="Percentage of reports meeting the program's SLA targets (HackerOne 90-day average)">
                        {formatEfficiency(program.response_efficiency_pct)}
                      </span>
                    </td>
                    <td className="text-chariot-text-secondary px-3 py-2">
                      {program.scope_count}
                    </td>
                    <td className="px-3 py-2">
                      <ScopeBadges summary={program.scope_type_summary} />
                    </td>
                    <td className="px-3 py-2">
                      {isApproved ? (
                        <Badge variant="outline">Approved</Badge>
                      ) : !program.verified ? (
                        <span className="text-chariot-text-secondary text-xs">
                          Unverified
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="default"
                          disabled={!canOnboardCustomer}
                          onClick={() => setProgramToApprove(program)}
                        >
                          Approve
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog
        open={!!programToApprove}
        onOpenChange={open => {
          if (!open) setProgramToApprove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Bug Bounty Program</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new BUG_BOUNTY customer for{' '}
              <strong>{programToApprove?.name}</strong> (
              {programToApprove?.platform}) and begin importing{' '}
              {programToApprove?.scope_count} scope items from HackerOne.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleApprove} disabled={isPending}>
              {isPending ? 'Approving...' : 'Approve'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
